import type { SearchTitle, SearchPerson } from "../apiRequests/searchTitles";
import { isUnknownArray, errorName } from "../types/data";
import React, { useState, useEffect, useRef } from "react";
import { useAppDispatch as useDispatch } from "../store/hooks";
import { Link, useNavigate } from "react-router-dom";
import "../utility/firestoreGateway";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";
import { useUserData } from "../hooks/useUserData";
import { searchTitles } from "../apiRequests/searchTitles";
import { getRatingToneClass } from "../utility/ratingColor";
import { formatPublicRating } from "../utility/ratingDisplay";
import { useI18n } from "../i18n/useI18n";
import { buildFilmPagePath } from "../utility/activityNavigation";
import { getLocalizedMovieTitle } from "../utility/localizedMovieTitle";
import { FilmMetadata } from "../types/media";

const PERSON_DEPARTMENT_LABELS: Record<string, string> = {
  Acting: "Актёрское мастерство",
  Directing: "Режиссура",
  Writing: "Сценарий",
  Production: "Продюсирование",
  Sound: "Музыка и звук",
  Camera: "Операторская работа",
  Editing: "Монтаж",
  Art: "Художественный отдел",
  CostumeAndMakeUp: "Костюмы и грим",
  Crew: "Съёмочная группа"
};

const getResultTypeLabel = (result: SearchTitle) => {
  if (result.media_type === "person") return "Персона";
  if (result.media_type === "tv" || result.first_air_date) return "Сериал";
  return "Фильм";
};

const getPersonDescription = (person: SearchPerson, language: string|undefined) => {
  const department = PERSON_DEPARTMENT_LABELS[person.known_for_department || ""] ||
    person.known_for_department || "Кино";
  const knownForTitles = (person.known_for || [])
    .map((item: FilmMetadata) => getLocalizedMovieTitle(item, language, ""))
    .filter(Boolean)
    .slice(0, 2);

  return [department, knownForTitles.join(", ")].filter(Boolean).join(" · ");
};

const SHORT_SEARCH_LIMIT = 5;

function SearchMovie({ onUserInteraction }: { onUserInteraction?: () => void }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useUserData();
  const { language, t } = useI18n();
  const [showMovieList, setShowMovieList] = useState(false);
  const [movieList, setMovieList] = useState<SearchTitle[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchStatus, setSearchStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const searchControllerRef = useRef<AbortController | null>(null);
  const searchRequestIdRef = useRef(0);

  const getMovieList = (value: string) => {
    onUserInteraction?.();
    setSearchValue(value);
    setMovieList([]);
    setSearchStatus(value.trim() ? "loading" : "idle");
    clearTimeout(searchTimerRef.current);
    searchControllerRef.current?.abort();
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;

    if (!value.trim()) {
      setShowMovieList(false);
      return;
    }

    setShowMovieList(false);

    searchTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      searchControllerRef.current = controller;
      try {
        const topResults = await searchTitles(value, {
          signal: controller.signal,
          pages: 3,
          limit: SHORT_SEARCH_LIMIT,
          includePeople: true,
          language
        });
        if (requestId !== searchRequestIdRef.current || controller.signal.aborted) return;
        setMovieList(isUnknownArray(topResults) ? topResults : []);
        setSearchStatus("done");
        setShowMovieList(true);
      } catch (error) {
        if (errorName(error) === "AbortError") return;
        if (requestId !== searchRequestIdRef.current || controller.signal.aborted) return;
        console.log(error);
        setSearchStatus("error");
        setShowMovieList(false);
      }
    }, 300);
  };
  const movieListBlock = useRef<HTMLDivElement | null>(null);
  const handleClickOutside = (event: MouseEvent) => {
    if (
      movieListBlock.current && event.target instanceof Node &&
      !movieListBlock.current.contains(event.target)
    ) {
      setShowMovieList(false);
    }
  };

  function selectResult(result: SearchTitle) {
    setSearchValue(result.media_type === "person"
      ? result.name || result.original_name || ""
      : getLocalizedMovieTitle(result, language, ""));
    setShowMovieList(false);
    if (result.media_type === "person") return;
    dispatch({
      type: "setSelectedFilm",
      payload: result
    });
  }

  function toggleShowMovieList() {
    onUserInteraction?.();
    if (
      !searchValue.trim() ||
      searchStatus === "loading" ||
      (searchStatus !== "done" && movieList.length === 0)
    ) {
      setShowMovieList(false);
      return;
    }

    setShowMovieList((value) => !value);
  }

  function openFullSearch() {
    if (!userId) return;
    clearTimeout(searchTimerRef.current);
    searchControllerRef.current?.abort();
    searchRequestIdRef.current += 1;
    setShowMovieList(false);
    const normalizedValue = searchValue.trim();
    navigate(
      normalizedValue
        ? `/${userId}/search?query=${encodeURIComponent(normalizedValue)}`
        : `/${userId}/search`
    );
  }

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      clearTimeout(searchTimerRef.current);
      searchControllerRef.current?.abort();
    };
  }, []);

  return (
    <div className="search-movie" data-onboarding-target="search">
      <div className="input-search">
        <input
          className="input-search__input"
          type="text"
          onFocus={() => onUserInteraction?.()}
          onChange={(e) => getMovieList(e.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            openFullSearch();
          }}
          value={searchValue}
          placeholder={t("фильм или персона")}
        />
        <button
          className="icons dropdown-icon"
          onClick={toggleShowMovieList}
        >
          {(showMovieList && movieList.length) || showMovieList ? (
            <IoChevronUpOutline />
          ) : (
            <IoChevronDownOutline />
          )}
        </button>
        {showMovieList && (
          <div ref={movieListBlock} className="movie-list__block">
            {movieList.length === 0 && searchStatus === "done" ? (
              <div className="movie-list__empty" role="status">
                {t("Ничего не найдено.")}
              </div>
            ) : movieList.map((movie) => {
              const isPerson = movie.media_type === "person";
              const year =
                movie.release_date?.slice(0, 4) || movie.first_air_date?.slice(0, 4);
              const typeLabel = getResultTypeLabel(movie);
              const ratingValue = Number(movie.vote_average);
              const imagePath = isPerson ? movie.profile_path : movie.poster_path;
              const personDescription = isPerson
                ? getPersonDescription(movie, language).split(" · ").map((part) => t(part)).join(" · ")
                : "";
              const resultTitle = isPerson
                ? movie.name || movie.original_name || t("Без имени")
                : getLocalizedMovieTitle(movie, language, t("Без названия"));

              return (
              <div
                key={`${movie.media_type || "title"}-${movie.id}`}
                className={`movie-item${isPerson ? " movie-item--person" : ""}`}
              >
                <Link
                  className="movie-item__link"
                  onClick={() => selectResult(movie)}
                  to={isPerson ? `/person/${movie.id}` : buildFilmPagePath(movie)}
                >
                  <span className="movie-item__poster-slot">
                    {imagePath ? (
                      <img
                        className="movie-item__image"
                        src={`https://image.tmdb.org/t/p/w185${imagePath}`}
                        srcSet={`https://image.tmdb.org/t/p/w92${imagePath} 92w, https://image.tmdb.org/t/p/w185${imagePath} 185w`}
                        sizes="70px"
                        alt=""
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className="movie-item__image movie-item__image--empty">
                        {isPerson ? t("Нет фото") : t("Нет постера")}
                      </span>
                    )}
                  </span>
                  <div className="film-info">
                    <div className="movie-item__name-row">
                      <span className="movie-item__name">
                        {resultTitle}
                      </span>
                      <span className={`movie-item__type movie-item__type--${movie.media_type || "movie"}`}>
                        {t(typeLabel)}
                      </span>
                    </div>
                    <div className="bottom-line">
                      {!isPerson && (
                        <span className={`movie-item__rating ${getRatingToneClass(ratingValue)}`}>
                          ★ {formatPublicRating(ratingValue)}
                        </span>
                      )}
                      <span className="movie-item__release-date">
                        {isPerson ? personDescription : year}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
              );
            })}
            {movieList.length > 0 && (
              <button
                type="button"
                className="input-search__show-all"
                onClick={openFullSearch}
              >
                {t("Показать все результаты ...")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
export default SearchMovie;
